/**
 * Keeping a signed-in session alive across an expiring access token.
 *
 * Three separate faults lived in the interceptor this replaces, and the first one hid the
 * other two:
 *
 * 1. THE URL WAS WRONG IN PRODUCTION. The refresh was posted to
 *    `` `${BASE_URL}/api/auth/token/refresh/` ``, and BASE_URL is `/` when the SPA and the
 *    API share an origin — so the string became `//api/auth/token/refresh/`, which a browser
 *    reads as PROTOCOL-RELATIVE and sends to a host literally named `api`. It never reached
 *    the server: two days of production logs contain no refresh request at all. Every
 *    session therefore ended 30 minutes after sign-in, when the access token expired and the
 *    "recovery" failed against a host that does not exist.
 *
 * 2. THE ROTATED REFRESH TOKEN WAS DISCARDED. `ROTATE_REFRESH_TOKENS` is on and so is
 *    `BLACKLIST_AFTER_ROTATION`: every refresh returns a NEW refresh token and blacklists
 *    the one that was used. The old code stored only `access`, so the second refresh would
 *    always present a blacklisted token and fail — the fix for (1) alone would have moved
 *    the logout from 30 minutes to 60.
 *
 * 3. CONCURRENT REFRESHES RACED. A page makes many requests at once; when the token expires
 *    they all 401 together and each would refresh with the same token. With rotation, the
 *    first succeeds and blacklists it, and every other one fails — logging the user out
 *    precisely when the app is busiest. Refresh is single-flight here: the first caller
 *    performs it and the rest await the same promise.
 */
import axios from 'axios';

/** Must match the keys `AuthContext` writes. */
const TOKENS_KEY = 'auth_tokens';
const USER_KEY = 'auth_user';

export interface StoredTokens {
  access?: string;
  refresh?: string;
}

export function readTokens(): StoredTokens {
  try {
    return JSON.parse(localStorage.getItem(TOKENS_KEY) || '{}');
  } catch {
    return {};
  }
}

export function writeTokens(tokens: StoredTokens): void {
  try {
    localStorage.setItem(TOKENS_KEY, JSON.stringify(tokens));
  } catch {
    // A full or blocked store must not break the request that just succeeded.
  }
}

export function clearSession(): void {
  try {
    localStorage.removeItem(TOKENS_KEY);
    localStorage.removeItem(USER_KEY);
  } catch {
    /* nothing useful to do */
  }
}

/**
 * Join the configured base with a path, without producing `//`.
 *
 * The whole of fault (1) was one string concatenation. `baseURL` is `/` on a same-origin
 * deployment and an absolute origin in development, and only one of those survives being
 * glued to a leading-slash path.
 */
export function apiUrl(baseUrl: string, path: string): string {
  const base = (baseUrl || '').replace(/\/+$/, '');
  const rest = path.startsWith('/') ? path : `/${path}`;
  return `${base}${rest}`;
}

//: The in-flight refresh, shared by every caller that arrives while it runs.
let inFlight: Promise<string> | null = null;

/**
 * Exchange the stored refresh token for a new access token, once.
 *
 * Returns the new access token. Throws if there is nothing to refresh with or the server
 * refuses — the caller decides whether that means signing out.
 */
export function refreshAccessToken(baseUrl: string): Promise<string> {
  if (inFlight) return inFlight;

  inFlight = (async () => {
    const { refresh } = readTokens();
    if (!refresh) throw new Error('No refresh token stored.');

    // A bare axios call, never the app's instance: going through that would re-enter the
    // response interceptor on a 401 and refresh in order to refresh.
    const response = await axios.post<{ access: string; refresh?: string }>(
      apiUrl(baseUrl, '/api/auth/token/refresh/'),
      { refresh },
    );

    const next: StoredTokens = { access: response.data.access };
    // Keep the ROTATED refresh token. Rotation is on and the one just used is now
    // blacklisted, so storing only `access` guarantees the next refresh fails.
    next.refresh = response.data.refresh || refresh;
    writeTokens(next);
    return next.access as string;
  })();

  // Cleared either way, so a failure does not pin every later refresh to the same rejection.
  inFlight = inFlight.finally(() => { inFlight = null; }) as Promise<string>;
  return inFlight;
}

/** Exported for tests, and for a sign-out that must not leave a stale promise behind. */
export function resetRefreshState(): void {
  inFlight = null;
}
